const DAY_MAP = { T2: 1, T3: 2, T4: 3, T5: 4, T6: 5, T7: 6, CN: 7 };
const DAY_LABELS = {
  T2: "Thứ 2",
  T3: "Thứ 3",
  T4: "Thứ 4",
  T5: "Thứ 5",
  T6: "Thứ 6",
  T7: "Thứ 7",
  CN: "Chủ nhật",
};

export const SCHEDULE_DAY_OPTIONS = [
  { value: "T2", label: "Thứ 2" },
  { value: "T3", label: "Thứ 3" },
  { value: "T4", label: "Thứ 4" },
  { value: "T5", label: "Thứ 5" },
  { value: "T6", label: "Thứ 6" },
  { value: "T7", label: "Thứ 7" },
  { value: "CN", label: "Chủ nhật" },
];

export const TIME_SLOT_OPTIONS = [
  { value: "07:00-09:00", label: "07:00 - 09:00" },
  { value: "09:00-11:00", label: "09:00 - 11:00" },
  { value: "13:30-15:30", label: "13:30 - 15:30" },
  { value: "15:30-17:30", label: "15:30 - 17:30" },
  { value: "18:00-20:00", label: "18:00 - 20:00" },
  { value: "19:00-21:00", label: "19:00 - 21:00" },
];

const PER_DAY_SEGMENT_RE =
  /^(T2|T3|T4|T5|T6|T7|CN)\|(\d{2}:\d{2}-\d{2}:\d{2})$/;

function isPerDayFormat(str) {
  const segments = String(str || "")
    .trim()
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return (
    segments.length > 0 &&
    segments.every((s) => PER_DAY_SEGMENT_RE.test(s))
  );
}

function parseTimeSlot(timePart) {
  let startH = 8;
  let endH = 10;
  const m = String(timePart || "").match(
    /(\d{1,2}):?(\d{0,2})?\s*-\s*(\d{1,2}):?(\d{0,2})?/
  );
  if (m) {
    startH = parseInt(m[1], 10) || 8;
    endH = parseInt(m[3], 10) || 10;
  }
  return { start: startH, end: endH };
}

function dayCodeToNum(code) {
  const d = String(code || "").trim();
  if (DAY_MAP[d] != null) return DAY_MAP[d];
  return null;
}

export function parseClassTimeValue(raw) {
  const str = String(raw || "").trim();
  const emptyRows = SCHEDULE_DAY_OPTIONS.map((d) => ({
    dayCode: d.value,
    label: d.label,
    enabled: false,
    timeSlot: null,
  }));

  if (!str) return { rows: emptyRows, classTime: "" };

  if (isPerDayFormat(str)) {
    const map = {};
    str.split(",").forEach((segment) => {
      const [dayCode, timeSlot] = segment.trim().split("|").map((s) => s.trim());
      if (dayCode && timeSlot) map[dayCode] = timeSlot;
    });
    const rows = SCHEDULE_DAY_OPTIONS.map((d) => ({
      dayCode: d.value,
      label: d.label,
      enabled: Boolean(map[d.value]),
      timeSlot: map[d.value] || null,
    }));
    return { rows, classTime: str };
  }

  if (str.includes("|")) {
    const [rawDays, rawSlot] = str.split("|");
    const daySet = new Set(
      rawDays
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean)
    );
    const rows = SCHEDULE_DAY_OPTIONS.map((d) => ({
      dayCode: d.value,
      label: d.label,
      enabled: daySet.has(d.value),
      timeSlot: daySet.has(d.value) ? rawSlot?.trim() || null : null,
    }));
    return { rows, classTime: str };
  }

  return { rows: emptyRows, classTime: str };
}

export function buildClassTimeFromRows(rows) {
  const active = (Array.isArray(rows) ? rows : [])
    .filter((r) => r.enabled && r.timeSlot)
    .sort((a, b) => {
      const order = { T2: 2, T3: 3, T4: 4, T5: 5, T6: 6, T7: 7, CN: 8 };
      return (order[a.dayCode] || 99) - (order[b.dayCode] || 99);
    });

  if (!active.length) return "";

  const slots = new Set(active.map((r) => r.timeSlot));
  if (slots.size === 1 && active.length > 1) {
    return `${active.map((r) => r.dayCode).join(",")}|${active[0].timeSlot}`;
  }
  return active.map((r) => `${r.dayCode}|${r.timeSlot}`).join(",");
}

export function formatClassTimeDisplay(raw) {
  const str = String(raw || "").trim();
  if (!str) return "—";

  if (isPerDayFormat(str)) {
    return str
      .split(",")
      .map((segment) => {
        const [dayCode, slot] = segment.trim().split("|");
        const label = DAY_LABELS[dayCode] || dayCode;
        return `${label}: ${slot}`;
      })
      .join(" · ");
  }

  if (!str.includes("|")) return str;
  const [daysPart, slotPart] = str.split("|");
  const days = (daysPart || "")
    .split(",")
    .map((d) => d?.trim())
    .filter(Boolean)
    .join(", ");
  return `${days}${slotPart ? ` • ${slotPart}` : ""}`;
}

export function getClassTimeListParts(raw) {
  const { rows } = parseClassTimeValue(raw);
  const active = rows.filter((r) => r.enabled && r.timeSlot);
  if (!active.length) {
    const display = formatClassTimeDisplay(raw);
    const parts = display.split(" • ");
    return {
      days: parts[0]?.trim() || display,
      schedule: parts[1]?.trim() || "",
    };
  }

  const slots = [...new Set(active.map((r) => r.timeSlot))];
  return {
    days: active.map((r) => r.dayCode).join(", "),
    schedule:
      slots.length === 1
        ? slots[0]
        : active.map((r) => `${r.dayCode} ${r.timeSlot}`).join(", "),
  };
}

/** @deprecated use parseClassTimeValue */
export function parseLegacyClassTime(raw) {
  const { rows } = parseClassTimeValue(raw);
  const days = rows
    .filter((r) => r.enabled)
    .map((r) => SCHEDULE_DAY_OPTIONS.find((d) => d.value === r.dayCode))
    .filter(Boolean);
  const slots = [...new Set(rows.filter((r) => r.enabled).map((r) => r.timeSlot))];
  const slot =
    slots.length === 1
      ? TIME_SLOT_OPTIONS.find((s) => s.value === slots[0]) || null
      : null;
  return { days, slot };
}
